<template>
  <div v-if="isStreaming || calls.length" class="stage-trail">
    <div v-if="isStreaming" class="flex items-center stage-trail-live">
      <el-icon class="is-loading stage-trail-spinner"><loading-icon /></el-icon>
      <span data-testid="ai-chat-stage-trail-current">{{ current }}</span>
    </div>
    <div v-if="calls.length" class="flex items-center stage-trail-summary">
      <span data-testid="ai-chat-stage-trail-summary">
        {{
          t(
            isStreaming
              ? 'aichat.main.stages.working'
              : 'aichat.main.stages.answered',
            { count: calls.length }
          )
        }}
        <template v-if="seconds">
          {{ t('aichat.main.stages.seconds', { seconds }) }}
        </template>
      </span>
      <el-tag
        v-if="isRefused"
        size="small"
        type="danger"
        effect="plain"
        data-testid="ai-chat-stage-trail-refused-tag"
      >
        {{ t('aichat.main.stages.refused') }}
      </el-tag>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { LoadingIcon } from '@shared/assets/icons'
import { useFormatNumber } from '@shared/plugins/number-formatter'
import { toolCalls } from '../utils/turn'
import {
  DEFAULT_STAGE_MESSAGE,
  STAGE_MESSAGES
} from '../constants/stage-messages'
import type { Turn } from '../types'

/* the stage events of a turn: what the agent is doing now, and afterwards how many tool calls answered */
const props = defineProps<{
  turn: Turn
}>()

const { t, locale } = useI18n()
const formatNumber = useFormatNumber()

const isStreaming = computed(() => props.turn.status === 'streaming')
const calls = computed(() => toolCalls(props.turn))

const current = computed(() => {
  const stage = props.turn.stages.at(-1)?.stage
  const key = (stage && STAGE_MESSAGES[stage]) || DEFAULT_STAGE_MESSAGE
  return t(`aichat.main.stages.${key}`)
})

/* e.g. 2.4 in en-US, 2,4 in de-DE */
const seconds = computed(() => {
  if (!props.turn.finishedAt) return ''
  return formatNumber((props.turn.finishedAt - props.turn.startedAt) / 1000, {
    locale: locale.value,
    precision: 1
  })
})

/* a tool result carried MCP's `isError`; tool names and arguments are never shown to the merchant */
const isRefused = computed(() =>
  props.turn.stages.some(
    (stage) =>
      stage.stage === 'tool_result' && 'isError' in stage && stage.isError
  )
)
</script>

<style scoped>
.stage-trail {
  align-self: flex-start;
  max-width: 80%;
}

.stage-trail-live {
  gap: 0.375rem;
  margin: 0.25rem 0;
  color: var(--el-text-color-secondary);
  font-size: 0.8125rem;
}

.stage-trail-spinner {
  width: 0.875rem;
  height: 0.875rem;
}

.stage-trail-summary {
  gap: 0.375rem;
  color: var(--el-text-color-secondary);
  font-size: 0.75rem;
}
</style>
